import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  InstrumentAmount as Amount,
  useInstrumentWithDetails,
} from '@tokenized/sdk-react-private';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import styles from './InstrumentAmount.scss';
import classNames from 'classnames';

function InstrumentAmountElement({
  renderAs = 'span',
  type,
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles[type],
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <span className={quantityClassName}>{children}</span>
    </Element>
  );
}

const minusFeesStyle = (chunks) => (
  <span className={styles.minusFees}>{chunks}</span>
);
const availableStyle = (chunks) => (
  <span className={styles.available}>{chunks}</span>
);
const eachStyle = (chunks) => <span className={styles.each}>{chunks}</span>;

function InstrumentAmountMinusFeesElement({
  renderAs = 'span',
  type,
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles[type],
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="{quantity} <info>(minus fees)</info>"
        description="Display quantity when sending whole BSV balance (minus fees for the transfer)"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
          info: minusFeesStyle,
        }}
      />
    </Element>
  );
}

function InstrumentAmountElementConversion({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.conversion,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="≈ {quantity}"
        description="Display approximate (rounded) conversion of instrument quantity to display currency"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
        }}
      />
    </Element>
  );
}

function InstrumentAmountElementReserved({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.reserved,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="{quantity} reserved"
        description="Reserved amount of instrument balance"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
        }}
        info
      />
    </Element>
  );
}

function InstrumentAmountElementAvailable({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.available,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="{quantity} <info>available</info>"
        description="Available amount of instrument balance"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
          info: availableStyle,
        }}
      />
    </Element>
  );
}

function InstrumentAmountElementTotal({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.total,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="Total {quantity}"
        description="Display total authorized instruments underneath delta"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
        }}
      />
    </Element>
  );
}

function InstrumentAmountElementUnitValue({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.unitValue,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="{quantity} <info>each</info>"
        description="Display approximate (rounded) conversion of instrument unit value to display currency"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
          info: eachStyle,
        }}
      />
    </Element>
  );
}

function InstrumentAmountElementFaceValue({
  renderAs = 'span',
  size,
  containerClassName,
  className: quantityClassName,
  noStyles,
  test,
  instrument,
  children,
}) {
  const Element = renderAs;

  return (
    <Element
      className={classNames(
        !noStyles && styles.quantity,
        styles.unitValue,
        size && styles[`is-${size}`],
        containerClassName,
      )}
      data-test={test}
      data-instrument-id={instrument?.instrumentId || undefined}
      data-instrument-type={instrument?.instrumentType || undefined}
      data-instrument-currency={instrument?.instrumentCurrencyCode || undefined}
    >
      <FormattedMessage
        defaultMessage="Face value = {quantity}"
        description="Display instrument unit face value in the currency specified by the contract"
        values={{
          quantity: <span className={quantityClassName}>{children}</span>,
        }}
      />
    </Element>
  );
}

export default function InstrumentAmount({
  instrument,
  type = 'balance',
  renderAs = 'span',
  size,
  className,
  hideCurrency,
  showCurrencyCodeOnRight,
  allowCurrencySymbolAndCode,
  showDisplayCurrency,
  useCustomPrecisionForDisplayCurrency = false,
  increasePrecisionForTinyNumbers = false,
  showWhenZero,
  showUnconvertibleAmountAsZero,
  numberFormatOverrides,
  splitLongFractions = true,
  enforceCompleteFractionGroups = false,
  typographicMinusSigns = true,
  spaceAroundSigns = true,
  forceSpaceAroundCurrency = true,
  noStyles,
  test,
}) {
  const amountCommonProps = {
    instrument,
    hideCurrency,
    showCurrencyCodeOnRight,
    allowCurrencySymbolAndCode,
    showWhenZero: showWhenZero ?? true,
    showDisplayCurrency:
      showDisplayCurrency || type === 'displayCurrencyConversion',
    showDisplayCurrencyWhenSame:
      showDisplayCurrency && type !== 'displayCurrencyConversion',
    useCustomPrecisionForDisplayCurrency,
    increasePrecisionForTinyNumbers,
    showUnconvertibleAmountAsZero,
    numberFormatOverrides,
    splitLongFractions,
    enforceCompleteFractionGroups,
    typographicMinusSigns,
    spaceAroundSigns,
    forceSpaceAroundCurrency,
  };
  const renderAsElementProps = {
    renderAs,
    type,
    size,
    containerClassName: className,
    noStyles,
    test,
    instrument,
  };

  switch (type) {
    default:
    case 'balance':
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElement}
          renderAsElementProps={renderAsElementProps}
        />
      );
    case 'transfer':
    case 'transferMinusFees':
    case 'authorized':
      return (
        <Amount
          {...amountCommonProps}
          showWhenZero={false}
          numberFormatOverrides={{
            ...amountCommonProps.numberFormatOverrides,
            signDisplay: 'always',
          }}
          renderAs={
            type === 'transferMinusFees'
              ? InstrumentAmountMinusFeesElement
              : InstrumentAmountElement
          }
          renderAsElementProps={renderAsElementProps}
        />
      );
    case 'displayCurrencyConversion': {
      return (
        <Amount
          {...amountCommonProps}
          numberFormatOverrides={{
            ...amountCommonProps.numberFormatOverrides,
            signDisplay: 'never',
          }}
          increasePrecisionForTinyNumbers={
            increasePrecisionForTinyNumbers || instrument?.isBsv
          }
          renderAs={InstrumentAmountElementConversion}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
    case 'available': {
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElementAvailable}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
    case 'reserved': {
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElementReserved}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
    case 'total': {
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElementTotal}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
    case 'unitValue': {
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElementUnitValue}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
    case 'faceValue': {
      return (
        <Amount
          {...amountCommonProps}
          renderAs={InstrumentAmountElementFaceValue}
          renderAsElementProps={renderAsElementProps}
        />
      );
    }
  }
}

export function InstrumentAmountFromOptions({ instrumentOptions, ...props }) {
  const profileId = useSelectedProfileId();
  const instrument = useInstrumentWithDetails(instrumentOptions, profileId);
  return <InstrumentAmount instrument={instrument} {...props} />;
}
